import {
  BREAKPOINT_ID_XXL,
  BREAKPOINT_ID_XL,
  BREAKPOINT_ID_LARGE,
  BREAKPOINT_ID_MAIN,
  BREAKPOINT_ID_MEDIUM,
  BREAKPOINT_ID_SMALL,
  BREAKPOINT_ID_TINY,
} from '@packages/systems/style/breakpoint-ids';
import type {BreakpointID} from '@packages/systems/style/types';
import type {
  PseudoStateKey,
  CustomNodeStateKey,
} from '@packages/systems/core/utils/StyleTypes';

import {cacheMemo} from '@packages/utilities/memo';
import {
  STATES_ORDER,
  PSEUDOS_ORDER,
} from '@packages/systems/core/constants/Selectors';
import {getOrderedBreakpointIds} from '@packages/systems/style/media-queries';
import {
  OPEN,
  CURRENT,
  NO_PSEUDO,
  HOVER,
  ACTIVE,
  BACKDROP,
  VISITED,
  FOCUS,
  PLACEHOLDER,
  FIRST_CHILD,
  LAST_CHILD,
  ODD_CHILD,
  EVEN_CHILD,
  POPOVER_OPEN,
  PSEUDO_KEYS,
  CUSTOM_NODE_STATE_KEYS,
} from '@packages/systems/core/utils/StyleUtils';

import type {OrderedMap} from 'immutable';

import type {StyleBlockVariant} from '@packages/systems/style-legacy/records';
import {
  VariantStateKey,
  isVariantStateKey,
} from '@packages/systems/style-legacy/selectors';

type VariantKeyInput = {
  readonly mediaQuery?: null | BreakpointID;
  readonly customNodeState?: null | CustomNodeStateKey;
  readonly pseudoState?: null | PseudoStateKey;
  readonly variantState?: null | VariantStateKey;
};

const prefix = (value?: string | null) => (value ? `_${value}` : '');

function compareByArrayIndex<T>(array: Array<T>, lhsValue: T, rhsValue: T) {
  const lhsIndex = array.indexOf(lhsValue);
  const rhsIndex = array.indexOf(rhsValue);

  if (lhsIndex === rhsIndex) {
    return 0;
  }

  return lhsIndex > rhsIndex ? 1 : -1;
}

/**
 * Creates a string key that can look up entries in the StyleBlock.variants map.
 */
export const createVariantKey = ({
  mediaQuery,
  customNodeState,
  pseudoState,
  variantState,
}: VariantKeyInput = {}) =>
  (mediaQuery || 'main') +
  prefix(variantState) +
  prefix(customNodeState) +
  prefix(pseudoState === 'noPseudo' ? '' : pseudoState);

export type VariantObject = {
  mediaQuery: BreakpointID;
  customNodeState: null | CustomNodeStateKey;
  pseudoState: null | PseudoStateKey;
  variantState: null | VariantStateKey;
};

const memoizeParseVariantKey = cacheMemo(
  getOrderedBreakpointIds().length *
    PSEUDO_KEYS.length *
    CUSTOM_NODE_STATE_KEYS.length
);

/**
 * Parses a variant key and returns an object with broken-down parts.
 * @param  {String} variantKey The key from the StyleBlock.variants map, e.g. "main_current_hover"
 * @return {Object}            Parsed info, e.g. { mediaQuery: "main", customNodeState: "current", pseudoState: "hover" }
 */
export const parseVariantKey = memoizeParseVariantKey(
  (variantKey: string): VariantObject => {
    const parts = variantKey.split('_');

    // Some assumptions:
    //
    // - We do _not_ assume that the `variantState` key is in any particular
    //   location within the `parts` list.
    // - We _do_ assume that there is **only** one `variantState` key within the
    //   main `variantKey` encoding.
    // - Other `variantKey` parts **are** assumed to be in specific locations
    //
    // Thus, we must now search through our `parts` array until we find a
    // `VariantStateKey`, stash that away for use, then remove it from the
    // `parts` array so that positionally-determined parts can be determined as
    // usual.
    let variantState: null | VariantStateKey = null;
    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i] as string;
      if (isVariantStateKey(part)) {
        variantState = part;
        parts.splice(i, 1);
        break;
      }
    }

    const result = {
      // Variants are always prefixed by the media query key
      mediaQuery: parts.shift(),
      // The state is always the second part in the key (if there is a second part)
      customNodeState:
        // @ts-expect-error - TS2345 - Argument of type 'string' is not assignable to parameter of type '"current" | "open" | "redirected-checked" | "redirected-focus" | "redirected-focus-visible" | "ecommerce-pill-selected" | "ecommerce-pill-disabled" | "ecommerce-add-to-cart-disabled" | "ecommerce-buy-now-disabled"'.
        parts.length && STATES_ORDER.indexOf(parts[0]) >= 0
          ? parts.shift()
          : null,
      // The pseudo state is always last (if it exists)
      pseudoState: parts.length ? parts.shift() : null,
      variantState: variantState ?? null,
    } as const;

    if (process.env.NODE_ENV === 'development') {
      if (
        result.mediaQuery !== BREAKPOINT_ID_MAIN &&
        result.mediaQuery !== BREAKPOINT_ID_MEDIUM &&
        result.mediaQuery !== BREAKPOINT_ID_SMALL &&
        result.mediaQuery !== BREAKPOINT_ID_TINY &&
        result.mediaQuery !== BREAKPOINT_ID_XXL &&
        result.mediaQuery !== BREAKPOINT_ID_XL &&
        result.mediaQuery !== BREAKPOINT_ID_LARGE
      ) {
        console.warn(
          `Parsed media query from variant with unexpected value: ${result.mediaQuery}`
        );
      }
      if (
        result.customNodeState &&
        result.customNodeState !== OPEN &&
        result.customNodeState !== CURRENT
      ) {
        console.warn(
          `Parsed custom node state from variant with unexpected value: ${result.customNodeState}`
        );
      }
      if (
        result.pseudoState &&
        result.pseudoState !== NO_PSEUDO &&
        result.pseudoState !== HOVER &&
        result.pseudoState !== ACTIVE &&
        result.pseudoState !== BACKDROP &&
        result.pseudoState !== VISITED &&
        result.pseudoState !== FOCUS &&
        result.pseudoState !== PLACEHOLDER &&
        result.pseudoState !== FIRST_CHILD &&
        result.pseudoState !== LAST_CHILD &&
        result.pseudoState !== ODD_CHILD &&
        result.pseudoState !== EVEN_CHILD &&
        result.pseudoState !== POPOVER_OPEN
      ) {
        console.warn(
          `Parsed pseudo state from variant with unexpected value: ${result.pseudoState}`
        );
      }
    }

    // @ts-expect-error - TS2322 - Type '{ readonly mediaQuery: string | undefined; readonly customNodeState: string | null | undefined; readonly pseudoState: string | null | undefined; }' is not assignable to type 'VariantObject'.
    return result;
  }
);

/**
 * Sorts variants by custom node state, then pseudo state.
 * @param  {Immutable.Map}          variants    Variant data as stored inside a StyleBlock. ALL the variants must belong to the same media query.
 * @return {Immutable.OrderedMap}               The same variant data, but converted to an ordered list.
 */
export const sortVariants = (
  variants: OrderedMap<BreakpointID, StyleBlockVariant>
) =>
  variants
    .map((variant, key) => ({
      key,
      parts: parseVariantKey(key),
      variant,
    }))
    .sort((lhs, rhs) => {
      const leftParts = lhs.parts;
      const rightParts = rhs.parts;
      const leftMediaQuery = leftParts.mediaQuery;
      const rightMediaQuery = rightParts.mediaQuery;
      const leftState = leftParts.customNodeState;
      const rightState = rightParts.customNodeState;
      const leftPseudo = leftParts.pseudoState;
      const rightPseudo = rightParts.pseudoState;

      const querySortOrder = compareByArrayIndex(
        getOrderedBreakpointIds(),
        leftMediaQuery,
        rightMediaQuery
      );

      // If the media queries are the same, order by custom node state and then by pseudo state
      if (querySortOrder === 0) {
        return leftState === rightState
          ? // @ts-expect-error - TS2345 - Argument of type 'readonly ["lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within" | undefined, ... 10 more ..., "lang" | ... 16 more ... | undefined]' is not assignable to parameter of type '("lang" | "active" | "placeholder" | "focus" | "after" | "before" | "hover" | "empty" | "noPseudo" | "nth-child(odd)" | "nth-child(even)" | "first-child" | "last-child" | "pressed" | "visited" | "focus-visible" | "focus-within" | null)[]'.
            compareByArrayIndex(PSEUDOS_ORDER, leftPseudo, rightPseudo)
          : // @ts-expect-error - TS2345 - Argument of type 'readonly ["open", "current", "redirected-checked", "redirected-focus", "redirected-focus-visible", "ecommerce-pill-selected", "ecommerce-pill-disabled", "ecommerce-add-to-cart-disabled", "ecommerce-buy-now-disabled"]' is not assignable to parameter of type '(CustomNodeStateKey | null)[]'.
            compareByArrayIndex(STATES_ORDER, leftState, rightState);
      }

      // otherwise, make sure the media queries are in correct order:
      // main > medium > small > tiny
      return querySortOrder;
    })
    .map(({variant}) => variant);

/**
 * Compares two variant keys based on custom node state and pseudo state.
 *
 * This function is meant to be used as a compareFn when sorting variant
 * keys that we know belong to the same media query. The comparison does
 * not take media query into account.
 */
export function compareVariantKeysByStateAndPseudo(
  variantKeyA: string,
  variantKeyB: string
): number {
  return (
    getStateAndPseudoOrder(variantKeyA) - getStateAndPseudoOrder(variantKeyB)
  );
}

function getStateAndPseudoOrder(variantKey: string): number {
  const idx = variantKey.indexOf('_');
  if (idx === -1) {
    return 0;
  }
  const stateAndPseudo = variantKey.substring(idx + 1);
  // if the state and pseudo combination can't be found in the map,
  // that means the either or both values are not "valid"
  // we return 10000 to ensure invalid values are sorted last
  return ORDERED_PSEUDO_AND_STATE_COMBOS[stateAndPseudo] ?? 10000;
}

const ORDERED_PSEUDO_AND_STATE_COMBOS: Readonly<{
  [key: string]: number;
}> = getOrderedPseudoAndStateCombinations();

function getOrderedPseudoAndStateCombinations() {
  const states = ['', ...STATES_ORDER];
  const pseudos = ['', ...PSEUDOS_ORDER];

  const combos: Record<string, any> = {};
  let order = 0;

  for (const stateKey of states) {
    for (const pseudoKey of pseudos) {
      if (stateKey && pseudoKey) {
        combos[`${stateKey}_${pseudoKey}`] = order;
      } else if (pseudoKey) {
        combos[pseudoKey] = order;
      } else if (stateKey) {
        combos[stateKey] = order;
      }

      order++;
    }
  }

  return combos;
}
